import { useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  AgeRestriction,
  ContentSerializeStatus,
  DirittoExclusiveType,
  DisplayReservedHideContentsDiffDays,
  NEW_CONTENT_TIME,
  UP_CONTENT_TIME,
  ContestWinnerKeywords,
  DirittoKeyword,
} from '@diritto/common/content/constants';
import { ContentDto } from '@diritto/common/content/dtos/content.dto';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';

export const useContentState = (content: ContentDto | ContentPublicRelayDto) =>
  useMemo(() => {
    const now = new Date();
    const isNew = content.firstOpenedAt
      ? new Date(content.firstOpenedAt).getTime() >= now.getTime() - NEW_CONTENT_TIME
      : false;
    const isUpdated = content.latestEpisodeOpenedAt
      ? new Date(content.latestEpisodeOpenedAt).getTime() >= now.getTime() - UP_CONTENT_TIME
      : false;
    const isDirittoOnly = content.exclusiveType === DirittoExclusiveType.DirittoOnly;
    const isDirittoOriginal = content.exclusiveType === DirittoExclusiveType.DirittoOriginal;
    const { isProposalWanted } = content;
    const isCompleted = content.serializeStatus === ContentSerializeStatus.Completed;
    const isPublished = content.serializeStatus === ContentSerializeStatus.Published;
    const isAdult = content.ageRestriction === AgeRestriction.Adult;
    const isHidden = content.isHiddenContents;

    const isContestWinner = content.dirittoKeywords.some(it => ContestWinnerKeywords.includes(it as DirittoKeyword));

    const { reservedHideContentsAt } = content;
    const reservedHideContentsDiffDays =
      (reservedHideContentsAt &&
        Math.floor(DateTime.fromJSDate(new Date(reservedHideContentsAt)).diffNow('days').days)) ??
      -1;
    const showHiddenReservationBadge =
      reservedHideContentsDiffDays <= DisplayReservedHideContentsDiffDays && reservedHideContentsDiffDays >= 0;

    const shouldShowBadges =
      isNew ||
      isDirittoOriginal ||
      isDirittoOnly ||
      isUpdated ||
      isCompleted ||
      isPublished ||
      isContestWinner ||
      isAdult ||
      showHiddenReservationBadge ||
      isHidden;

    return {
      isNew,
      isUpdated,
      isDirittoOnly,
      isDirittoOriginal,
      isProposalWanted,
      isCompleted,
      isPublished,
      isAdult,
      isHidden,
      isContestWinner,
      showHiddenReservationBadge,
      reservedHideContentsDiffDays,
      shouldShowBadges,
    };
  }, [content]);
